import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { api, playState } from "../../lib/api";
import { useCatalog } from "../../hooks/useData";
import { MobileTouchInput } from "../../engine/input/InputProvider";
import { DEFAULT_MAPPING, MovementEngine } from "../../engine/MovementEngine";
import { PhoneFrame, useElementSize } from "../../components/game/PhoneFrame";
import { BaoInGame } from "../../components/game/BaoInGame";
import { GameHud } from "../../components/game/GameHud";
import { useI18n } from "../../i18n";
import { ReadAloud } from "../../components/brand/Experience";

const STEPS = [
  { key: "right_arm_raise_deg", label: "Levez le bras droit", metric: "shoulder_flexion", kind: "vertical", side: "right", unit: "°" },
  { key: "left_arm_raise_deg", label: "Levez le bras gauche", metric: "shoulder_flexion", kind: "vertical", side: "left", unit: "°" },
  { key: "reach_forward_cm", label: "Tendez le bras vers l'avant", metric: "reach_cm", kind: "vertical", side: "auto", unit: " cm" },
  { key: "reach_left_cm", label: "Tendez le bras vers la gauche", kind: "lateral", side: "left", unit: " cm" },
  { key: "reach_right_cm", label: "Tendez le bras vers la droite", kind: "lateral", side: "right", unit: " cm" },
  { key: "right_leg_lift_deg", label: "Levez la jambe droite", metric: "hip_flexion", kind: "vertical", side: "right", unit: "°", limb: "leg" },
  { key: "left_leg_lift_deg", label: "Levez la jambe gauche", metric: "hip_flexion", kind: "vertical", side: "left", unit: "°", limb: "leg" },
];

export default function Calibration() {
  const nav = useNavigate();
  const { t } = useI18n();
  const { sessionId, patientId } = playState.get();
  const { data: catalog } = useCatalog();
  const [stageRef, size] = useElementSize();
  const [i, setI] = useState(0);
  const [values, setValues] = useState({});
  const [live, setLive] = useState(null);
  const [hand, setHand] = useState(null);
  const [done, setDone] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const peak = useRef(0);
  const step = STEPS[i];

  useEffect(() => { if (!sessionId) nav("/patient"); else api.get(`/patients/${patientId}/assessments`).then((r) => setHasPrev(r.data.length > 0)); }, [sessionId, patientId, nav]);

  useEffect(() => {
    if (!stageRef.current || done) return;
    const mv = new MovementEngine(catalog?.coordinate_mapping || DEFAULT_MAPPING);
    const input = new MobileTouchInput();
    input.attach(stageRef.current);
    const unsub = input.subscribe((s) => {
      if (s.phase === "start") peak.current = 0;
      const v = step.kind === "lateral" ? mv.lateralReachFromX(s.x) : mv.amplitudeFromY(step.metric, s.y);
      const valid = step.kind !== "lateral" || mv.sideFromX(s.x) === step.side;
      if (valid) peak.current = Math.max(peak.current, v);
      const side = step.side === "auto" ? mv.sideFromX(s.x) : step.side;
      setHand(s.phase === "end" ? null : { side, x: s.x, y: s.y });
      setLive(Math.round(peak.current));
      if (s.phase === "end" && peak.current > 0) setValues((o) => ({ ...o, [step.key]: Math.round(peak.current) }));
    });
    return () => { unsub(); input.detach(); };
  }, [step, catalog, done, stageRef]);

  const finish = async (useLast) => {
    if (!useLast) await api.post(`/patients/${patientId}/calibrations`, { session_id: sessionId, values, input_source: "mobile_touch_simulated" });
    await api.post(`/sessions/${sessionId}/plan`);
    if (useLast) nav("/patient/map"); else setDone(true);
  };
  const validate = () => { if (i < STEPS.length - 1) { setI(i + 1); setLive(null); } else finish(false); };
  const stop = () => nav("/patient/end", { state: { stopped: true } });

  if (done) return (
    <PhoneFrame>
      <div className="flex h-full flex-col items-center justify-center gap-5 bg-[#0A2540] p-8 text-center text-white" data-testid="calibration-complete">
        <CheckCircle2 className="h-20 w-20 text-[#17A2A2]" />
        <h1 className="font-display text-4xl font-extrabold">{t("calibDone")}</h1>
        <p className="text-lg text-white/80">{t("calibDoneSub")}</p>
        <div className="grid w-full grid-cols-2 gap-2 text-left">
          {STEPS.map((s) => (
            <div key={s.key} className="rounded-2xl bg-white/5 p-3"><p className="text-[11px] text-white/60">{s.label}</p>
              <p className="font-mono text-lg font-bold text-[#4FD1C5]">{values[s.key] ?? "—"}{s.unit}</p></div>
          ))}
        </div>
        <p className="text-[10px] font-bold tracking-widest text-white/50">{t("simulated")}</p>
        <button data-testid="calibration-to-map-button" onClick={() => nav("/patient/map")} className="h-16 w-full rounded-full bg-brand text-lg font-extrabold">Entrer dans le monde de Bao</button>
      </div>
    </PhoneFrame>
  );

  return (
    <PhoneFrame>
      <GameHud onStop={stop} seeds={0} />
      <div ref={stageRef} className="stage absolute inset-0 bg-gradient-to-b from-[#BFEFEA] to-[#F3E6C8]" data-testid="calibration-stage">
        <div className="pointer-events-none top-hud-offset absolute inset-x-0 px-6 text-center">
          <p className="text-sm font-semibold text-[#0F7C7C]">{t("calibTitle")}</p>
          <p className="mt-1 text-xs font-bold text-slate-500">Étape {i + 1} / {STEPS.length}</p>
          <h2 data-testid="calibration-step-label" className="mt-3 font-display text-3xl font-semibold text-[#0A2540]">{step.label}</h2>
          <div className="pointer-events-auto mt-2"><ReadAloud testId="calibration-read-aloud" text={`${step.label}. Faites glisser votre doigt, aussi loin que confortable. Ne forcez pas.`} /></div>
          <p className="mt-2 text-sm text-slate-600">
            {step.kind === "lateral" ? "Faites glisser votre doigt horizontalement, aussi loin que confortable." : "Faites glisser votre doigt vers le haut, aussi loin que confortable."} Ne forcez pas.
          </p>
          <p data-testid="calibration-live-value" className="mt-4 font-mono text-5xl font-bold text-[#17A2A2]">{(values[step.key] ?? live) ?? "—"}{step.unit}</p>
        </div>
        <BaoInGame size={size} limb={step.limb || "arm"} hands={hand ? { [hand.side]: hand } : {}} />
      </div>
      <div className="absolute inset-x-0 bottom-0 z-30 flex flex-col gap-2 bg-gradient-to-t from-white/70 to-transparent safe-bottom p-4 pt-10">
        <div className="flex gap-2">
          <button data-testid="calibration-retry-button" onClick={() => { setValues((o) => ({ ...o, [step.key]: undefined })); setLive(null); }}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow"><RotateCcw /></button>
          <button data-testid="calibration-validate-button" disabled={!values[step.key]} onClick={validate}
            className="h-16 flex-1 rounded-full bg-brand text-lg font-extrabold text-white disabled:opacity-40">Valider</button>
        </div>
        {hasPrev && i === 0 && (
          <button data-testid="calibration-use-last-button" onClick={() => finish(true)} className="h-12 text-sm font-semibold text-[#0F7C7C] underline">Utiliser la dernière calibration</button>
        )}
      </div>
    </PhoneFrame>
  );
}
